import { useNavigate } from "@solidjs/router";
import { createMemo, createResource, createSignal, For, onMount, Show } from "solid-js";
import type { PaymentMethodView, TripHistoryItem, TripHistoryPage } from "../../shared/api";
import { ApiFailure, api } from "../api";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { ScreenLoader } from "../components/Spinner";
import { euros } from "../format";
import {
  amountLabel,
  groupHistory,
  paidWithLabel,
  rowSubtitle,
  rowTitle,
  seenRides,
} from "../history";
import { session } from "../state/session";

const PAGE = 20;

const errorText = (e: unknown) =>
  e instanceof ApiFailure && e.code === "RATE_LIMITED"
    ? "Trop de demandes. Réessayez dans un moment."
    : "Impossible de charger vos trajets pour le moment.";

/**
 * Figma H2 "Historique d'achat": bike rides only (M'Ticket purchases are out of the app's reach),
 * a summary card per month, day headers, one row per ride → H3. Pages of 20 with "Voir plus".
 */
export function History() {
  const navigate = useNavigate();
  const [items, setItems] = createSignal<TripHistoryItem[]>([]);
  const [next, setNext] = createSignal<number | undefined>();
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string>();
  const [method] = createResource(() =>
    api.get<PaymentMethodView | null>("/payments/method").catch(() => null),
  );

  const load = async (offset: number) => {
    setLoading(true);
    setError(undefined);
    try {
      const page = await api.get<TripHistoryPage>(`/trips?offset=${offset}&limit=${PAGE}`);
      for (const i of page.items) seenRides.set(i.id, i);
      // A ride can move to the next page while the list is open: keep each id once.
      setItems((prev) => {
        const ids = new Set(prev.map((i) => i.id));
        return [...prev, ...page.items.filter((i) => !ids.has(i.id))];
      });
      setNext(page.nextOffset);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  onMount(async () => {
    if (!(await session.known())) {
      navigate(`/signin?next=${encodeURIComponent("/historique")}`, { replace: true });
      return;
    }
    await load(0);
  });

  const months = createMemo(() => groupHistory(items(), Date.now()));

  return (
    <main class="screen bg-surface">
      <AppBar title="Historique d'achat" back="/" />
      <Show
        when={items().length > 0}
        fallback={
          <Show when={!loading()} fallback={<ScreenLoader label="Chargement des trajets…" />}>
            <Show when={error()} fallback={<Empty onFind={() => navigate("/map")} />}>
              {(msg) => <Failure text={msg()} onRetry={() => void load(0)} />}
            </Show>
          </Show>
        }
      >
        <div class="screen-body pb-[max(24px,env(safe-area-inset-bottom))]">
          <For each={months()}>
            {(m) => (
              <section>
                {/* Figma H2 month card: 372 x 74, lilac, radius 12. */}
                <div class="relative mx-[15px] mt-4 h-[74px] rounded-[12px] bg-surface2">
                  <h2 class="absolute left-4 top-3 text-sm font-medium leading-[1.25] tracking-[0.14px] text-violet">
                    {m.label}
                  </h2>
                  <p class="absolute left-4 top-[34px] w-[220px] text-xs leading-[1.25] tracking-[0.12px] text-text">
                    Tram et bus inclus dans votre Pass gratuité
                  </p>
                  <span class="absolute right-4 top-[18px] text-[26px] font-semibold leading-[1.25] tracking-[0.26px] text-violet">
                    {euros(m.totalCents)}
                  </span>
                </div>
                <For each={m.days}>
                  {(d, n) => (
                    <>
                      <h3
                        class={`${n() === 0 ? "mt-5" : "mt-[14px]"} px-5 text-[13px] font-medium leading-[1.25] tracking-[0.13px] text-strong`}
                      >
                        {d.label}
                      </h3>
                      <ul class="mt-[6px]">
                        <For each={d.items}>
                          {(i) => (
                            <li>
                              <RideRow
                                item={i}
                                method={paidWithLabel(i, method())}
                                onClick={() => navigate(`/historique/${encodeURIComponent(i.id)}`)}
                              />
                            </li>
                          )}
                        </For>
                      </ul>
                    </>
                  )}
                </For>
              </section>
            )}
          </For>
          <Show when={error()}>
            {(msg) => (
              <p class="mx-5 mt-4 text-center text-sm leading-[1.25] text-text" role="alert">
                {msg()}
              </p>
            )}
          </Show>
          <Show when={next() !== undefined}>
            <div class="mx-[15px] mt-5">
              <Button
                variant="outline"
                loading={loading()}
                onClick={() => {
                  const n = next();
                  if (n !== undefined) void load(n);
                }}
              >
                Voir plus
              </Button>
            </div>
          </Show>
        </div>
      </Show>
    </main>
  );
}

/**
 * Figma H2 ride row: 58 px, tam/mode-velo 30 px disc, title Medium 15 and subtitle Regular 12
 * (#666665), amount Medium 15, grey 22 px chevron, divider from 62 px to 15 px from the right.
 */
function RideRow(props: {
  item: TripHistoryItem;
  method: string | undefined;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      class="relative block h-[58px] w-full text-left active:bg-surface2"
      onClick={() => props.onClick()}
    >
      <span class="absolute left-5 top-3 flex h-[30px] w-[30px] items-center justify-center rounded-full bg-indigo">
        <img src="/icons/bike-white-36.svg" width="17" height="17" alt="" />
      </span>
      <span class="absolute left-[62px] right-[90px] top-2 truncate text-[15px] font-medium leading-[1.25] tracking-[0.15px] text-text">
        {rowTitle(props.item)}
      </span>
      <span class="absolute left-[62px] right-[90px] top-[29px] truncate text-xs leading-[1.25] tracking-[0.12px] text-text">
        {rowSubtitle(props.item, props.method)}
      </span>
      <span class="absolute right-[52px] top-[17px] text-[15px] font-medium leading-[1.25] tracking-[0.15px] text-text">
        {amountLabel(props.item)}
      </span>
      <img
        src="/icons/chevron-right-grey-22.svg"
        width="22"
        height="22"
        alt=""
        class="absolute right-6 top-4"
      />
      <span class="absolute bottom-0 left-[62px] right-[15px] border-b border-border" />
    </button>
  );
}

/** Not drawn in Figma: a short message and a way to the bikes. */
function Empty(props: { onFind: () => void }) {
  return (
    <div class="screen-body items-center justify-center px-8 pb-16 text-center">
      <span class="flex h-16 w-16 items-center justify-center rounded-full bg-indigo">
        <img src="/icons/bike-white-36.svg" width="36" height="36" alt="" />
      </span>
      <h2 class="mt-4 text-lg font-medium leading-[1.25] tracking-[0.18px] text-strong">
        Aucun trajet pour l'instant
      </h2>
      <p class="mt-2 text-sm leading-[1.25] tracking-[0.14px] text-text">
        Vos trajets à vélo et leurs reçus apparaîtront ici.
      </p>
      <div class="mt-6 w-full">
        <Button onClick={() => props.onFind()}>Trouver un vélo</Button>
      </div>
    </div>
  );
}

function Failure(props: { text: string; onRetry: () => void }) {
  return (
    <div class="screen-body items-center justify-center px-8 pb-16 text-center" role="alert">
      <img src="/icons/warning-red-44.svg" width="44" height="44" alt="" />
      <p class="mt-3 text-[15px] leading-[1.25] tracking-[0.15px] text-strong">{props.text}</p>
      <div class="mt-6 w-full">
        <Button variant="outline" onClick={() => props.onRetry()}>
          Réessayer
        </Button>
      </div>
    </div>
  );
}
